import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  useContext
} from "react";
import "./VoiceBotiFrame.css";
import { getAuth, getIdToken, onAuthStateChanged } from "firebase/auth";
import { VisemeActionsCtx } from "./VisemeContext";
import remap from "../config/phonemeRemap.json";
import { useVoiceBotSession } from "../voicebotSession/voiceBotSession";
import { appendActivationToUrl } from "../voicebotSession/voiceBotActivation";
import { markVoiceBotEnvelope, markVoiceBotFirstIdylate, postEnvelopeSnapshotToIframe } from "../voicebotSession/voiceBotEnvelope";
import { postSimPressureToIframe } from "../voicebotSession/voiceBotSimHarness";
import { shouldNotifyAvatarReload } from "../voicebotSession/voiceBotAvatarIds";
import { postTuningPayload, readStoredTuningPayload } from "../voicebotSession/voiceBotTuningSession";
import { noteStudioRender } from "./vbStudioRenderDebug";

const phonemes = [
  "—","Sh","F","K","Ch","V","B","Z","S","H",
  "N","P","D","T","M","θ","G","ð","Ʒ","uː","R",
  "ʤ","iː","ŋ","ɑː","ɪ","ʊ","əʊ","L","ɒ","ə",
  "ɛ","ʌ"
];

const applyRemap = (symbol) => remap[symbol] || symbol;

const TRUSTED_ORIGIN = "https://voicebot.ispeakwell.ca";
const TRUSTED_PARENT_PROD = "https://ispeakwell.ca";
const TRUSTED_PARENT_DEV  = "http://localhost:3000";

function arraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function postWorkToTools(work) {
  const toolsEl = document.getElementById("theBotTools");
  const toolsWin = toolsEl?.contentWindow;
  if (!toolsWin) return;
  toolsWin.postMessage({ type: "Work", value: work }, TRUSTED_ORIGIN);
}

const VoiceBotiFrame = React.memo(React.forwardRef(function VoiceBotiFrame(
  {
    onIdylateChange,
    onFFTChange,
    onAmpMagChange,
    onWorkChange,
    onLapChange,
    onAvatarReload,
    showTooltipAllowed,
  },
  forwardedRef
) {
  noteStudioRender("VoiceBotiFrame");

  const botIframeRef = useRef(null);   // <-- actual DOM iframe ref
  const { activation, envelopeEnabled, simProfile } = useVoiceBotSession();
  const visemeActions = useContext(VisemeActionsCtx);

  const [iframeSrc, setIframeSrc] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  const idylateRef = useRef(0);
  const ampMagRef  = useRef(0);
  const lapRef     = useRef(0);
  const workRef    = useRef(0);
  const fftRef     = useRef([]);
  const lastAvatarSrcRef = useRef("");
  const lastIdxRef = useRef(null);
  const phonemeHistoryRef = useRef([]);
  const tooltipTextRef = useRef(null);

  const recordVisemeChangeRef = useRef(visemeActions?.recordVisemeChange);
  const setCurrentVisemeRef = useRef(visemeActions?.setCurrentViseme);

  useEffect(() => {
    recordVisemeChangeRef.current = visemeActions?.recordVisemeChange;
    setCurrentVisemeRef.current = visemeActions?.setCurrentViseme;
  }, [visemeActions]);

  const idylCbRef = useRef(onIdylateChange);
  const fftCbRef  = useRef(onFFTChange);
  const ampCbRef  = useRef(onAmpMagChange);
  const lapCbRef  = useRef(onLapChange);
  const workCbRef = useRef(onWorkChange);
  const avatarReloadCbRef = useRef(onAvatarReload);

  useEffect(() => { idylCbRef.current = onIdylateChange; }, [onIdylateChange]);
  useEffect(() => { fftCbRef.current = onFFTChange; }, [onFFTChange]);
  useEffect(() => { ampCbRef.current = onAmpMagChange; }, [onAmpMagChange]);
  useEffect(() => { lapCbRef.current = onLapChange; }, [onLapChange]);
  useEffect(() => { workCbRef.current = onWorkChange; }, [onWorkChange]);
  useEffect(() => { avatarReloadCbRef.current = onAvatarReload; }, [onAvatarReload]);

  const syncWorkToLap = (nextWork, nextLap) => {
    let work = nextWork;
    const lap = nextLap;
    if (work < lap) {
      work = lap;
    }
    workRef.current = work;
    lapRef.current = lap;
    workCbRef.current?.(work);
    if (work > lap) {
      postWorkToTools(work);
    }
    return work;
  };

  const noteIdylate = (safe) => {
    const rawPh = phonemes[safe] ?? "—";
    const ph = applyRemap(rawPh);
    const history = phonemeHistoryRef.current;
    history.push(ph);
    if (history.length > 120) history.splice(0, history.length - 120);
    if (tooltipTextRef.current) {
      tooltipTextRef.current.textContent = history.join(", ");
    }

    const prevIdx = lastIdxRef.current;
    if (prevIdx != null && prevIdx !== safe) {
      recordVisemeChangeRef.current?.(prevIdx, safe);
    }
    lastIdxRef.current = safe;
    setCurrentVisemeRef.current?.(safe);
    idylCbRef.current?.(safe);
  };

  useEffect(() => {
    const auth = getAuth();

    const setSrcFor = async (u) => {
      if (!u) {
        setIframeSrc("");
        return;
      }
      try {
        const token = await getIdToken(u);
        const baseUrl = `${TRUSTED_ORIGIN}/get-html?authToken=${encodeURIComponent(token)}`;
        const next = appendActivationToUrl(baseUrl, activation, { includeAvatarParams: true });
        markVoiceBotEnvelope("parent:avatar-iframe-requested");
        setIframeSrc(next);
      } catch (e) {
        console.error("Error getting ID token:", e);
        setIframeSrc("");
      }
    };

    setSrcFor(auth.currentUser);
    const unsub = onAuthStateChanged(auth, setSrcFor);
    return () => unsub();
  }, [activation]);

  useEffect(() => {
    if (!iframeSrc) {
      lastAvatarSrcRef.current = "";
      return;
    }
    if (!shouldNotifyAvatarReload(lastAvatarSrcRef.current, iframeSrc)) return;
    lastAvatarSrcRef.current = iframeSrc;
    avatarReloadCbRef.current?.();
  }, [iframeSrc]);

  useEffect(() => {
    const trustedOrigin = iframeSrc ? new URL(iframeSrc).origin : TRUSTED_ORIGIN;

    const handleMessage = (event) => {
      if (event.origin !== trustedOrigin) return;

      const { type, value } = event.data || {};
      if (type == null) return;

      switch (type) {
        case "Idylate": {
          markVoiceBotFirstIdylate();
          const idx = Number(value);
          if (Number.isFinite(idx)) {
            const safe = Math.max(0, Math.min(32, Math.trunc(idx)));
            if (safe !== idylateRef.current) {
              idylateRef.current = safe;
              noteIdylate(safe);
            }
          }
          break;
        }

        case "ampMag": {
          const num = Number(value);
          if (Number.isFinite(num) && num !== ampMagRef.current) {
            ampMagRef.current = num;
            ampCbRef.current?.(num);
          }
          break;
        }

        case "Lap": {
          const num = Number(value);
          if (Number.isFinite(num) && num !== lapRef.current) {
            lapRef.current = num;
            lapCbRef.current?.(num);
            syncWorkToLap(workRef.current, num);
          }
          break;
        }

        case "Work": {
          const num = Number(value);
          if (Number.isFinite(num) && num !== workRef.current) {
            syncWorkToLap(num, lapRef.current);
          }
          break;
        }

        case "FFT": {
          const arr = Array.isArray(value) ? value.slice(0, 100) : [];
          if (!arraysEqual(arr, fftRef.current)) {
            fftRef.current = arr;
            fftCbRef.current?.(arr);
          }
          break;
        }

        case "Latency":
          console.log("Latency (ms):", value?.toFixed?.(2) ?? value);
          break;

        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeSrc]);

  // Parent window -> inner avatar iframe forwarding
  useEffect(() => {
    function onMessage(event) {
      if (
        event.origin !== TRUSTED_PARENT_PROD &&
        event.origin !== TRUSTED_PARENT_DEV
      ) {
        return;
      }

      const { type, bins, samples } = event.data || {};
      if (type === "ytFFT" && Array.isArray(bins)) {
        const botWin = botIframeRef.current?.contentWindow;
        if (!botWin) return;

        botWin.postMessage(
          { type: "ytFFT", bins },
          TRUSTED_ORIGIN
        );
      }
      if (type === "ytPcm16k" && samples) {
        const botWin = botIframeRef.current?.contentWindow;
        if (!botWin) return;
        botWin.postMessage({ type: "ytPcm16k", samples }, TRUSTED_ORIGIN);
      }
      if (type === "ytShareStopped") {
        const botWin = botIframeRef.current?.contentWindow;
        if (!botWin) return;
        botWin.postMessage({ type: "ytShareStopped" }, TRUSTED_ORIGIN);
      }
      if (type === "echoStage") {
        const botWin = botIframeRef.current?.contentWindow;
        if (!botWin) return;
        botWin.postMessage(
          { type: "echoStage", stage: event.data.stage || null },
          TRUSTED_ORIGIN
        );
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Expose a clean API to VB.js
  useImperativeHandle(forwardedRef, () => ({
    postMessage: (msg) => {
      const botWin = botIframeRef.current?.contentWindow;
      if (!botWin) return false;
      botWin.postMessage(msg, TRUSTED_ORIGIN);
      return true;
    }
  }), []);

  return (
    <div id="wrap">
      <div id="VoiceBotSetting">
        <iframe
          id="theBot"
          data-vb-owner="avatar"
          ref={botIframeRef}
          src={iframeSrc}
          title="VoiceBot"
          allow="microphone"
          onMouseEnter={() => setShowTooltip(false)}
          onMouseLeave={() => setShowTooltip(false)}
          onLoad={() => {
            markVoiceBotEnvelope("parent:avatar-iframe-load");
            const win = botIframeRef.current?.contentWindow;
            if (!win) return;
            if (envelopeEnabled) postEnvelopeSnapshotToIframe(win, TRUSTED_ORIGIN);
            if (simProfile && simProfile !== "off") {
              postSimPressureToIframe(win, TRUSTED_ORIGIN, simProfile);
            }
            const storedTuning = readStoredTuningPayload();
            if (storedTuning && Object.keys(storedTuning).length) {
              postTuningPayload(storedTuning, TRUSTED_ORIGIN);
            }
          }}
        />
        {showTooltipAllowed && (
          <div className={`tooltip ${showTooltip ? "show-tooltip" : ""}`}>
            <span className="dynamic-text" ref={tooltipTextRef} />
          </div>
        )}
      </div>
    </div>
  );
}));

export default VoiceBotiFrame;
